import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';

import { Currency } from '@/data/currencies/type';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { RecipientAccount } from '@/types/contacts';
import { Locale } from '@/types/locale';

interface Props {
  readonly account: RecipientAccount;
}

export const Recipient: React.FC<Props> = (props: Props): React.ReactElement => {
  const { account } = props;

  const tr = useTranslator();
  const locale = React.useContext<Locale>(LocaleContext);
  const currency = React.useMemo(
    (): Currency => Currency.fromCode(locale, account.currencyCode),
    [locale, account.currencyCode],
  );

  const Icon = React.useMemo(
    (): React.FunctionComponent<React.SVGProps<SVGSVGElement>> => currency.icon,
    [currency.icon],
  );

  return (
    <Box>
      <Typography variant="body2">{account.name}</Typography>
      {account.bankName && (
        <Typography variant="body1" color="text.secondary">
          Bank: {account.bankName}
        </Typography>
      )}
      {account.accountNumber && (
        <Typography variant="body1" color="text.secondary">
          Account/IBAN: {account.accountNumber}
        </Typography>
      )}
      <Box display="flex" alignItems="center" gap={1}>
        <Icon width={16} height={16} />
        <Typography variant="body1" color="text.secondary">
          {account.currency} - {tr(account.paymentMethod)}
        </Typography>
      </Box>
    </Box>
  );
};
